/* Cacao product page — the interactive sections.

   EVERYTHING HERE IS AN ENHANCEMENT.
   Content is fully rendered and readable before this file runs, and nothing in
   the stylesheet starts at opacity 0. Two classes gate the extras:

     .cacao-js    added whenever this module runs. Switches on controls that
                  would do nothing without script -- the compare slider, the
                  ritual tab strip, the calculator steppers.
     .cacao-anim  added only when the reader has NOT asked for reduced motion.
                  Everything that moves is behind this class, so reduced
                  motion gets the finished state with no movement at all.

   That split is deliberate: a reader with reduced motion still gets every
   interactive control, just without the transitions. Hiding content until an
   animation fires is what broke heatmaps and in-app browsers before, so no
   element here is invisible waiting for a class. */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = document.documentElement;
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

root.classList.add('cacao-js');
const setAnim = () => root.classList.toggle('cacao-anim', !reduced.matches);
setAnim();
reduced.addEventListener?.('change', setAnim);

const canAnimate = () => root.classList.contains('cacao-anim');

/** Run `fn` once, the first time `el` is near the viewport. */
function once(el, fn, margin = '0px 0px -10% 0px', threshold = 0) {
  if (!('IntersectionObserver' in window)) {
    fn(el);
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        io.unobserve(e.target);
        fn(e.target);
      }
    },
    { rootMargin: margin, threshold }
  );
  io.observe(el);
}

/* ---------------------------------------------------------------
   Scroll reveals
   --------------------------------------------------------------- */

function reveals() {
  if (!canAnimate()) return;

  const armed = [];
  const vh = window.innerHeight || 0;

  for (const el of document.querySelectorAll('.cacao-reveal')) {
    /* Never hide something that is already on screen. Arming it would make
       content vanish under the reader on a slow first paint. */
    if (el.getBoundingClientRect().top < vh) continue;
    el.classList.add('cacao-reveal--armed');
    armed.push(el);
    /* threshold 0, not a fraction: a section taller than the viewport can
       never reach a 12% ratio, so a fractional threshold left whole bands
       invisible on a phone. */
    once(el, (t) => t.classList.add('is-in'), '0px 0px -8% 0px');
  }

  /* The safety net. Hidden-until-animated content is the one failure mode
     that must not survive a flaky observer, a screenshot tool, a heatmap
     recorder or an in-app browser, so everything still armed is shown
     unconditionally a few seconds in, whether or not it was scrolled past. */
  setTimeout(() => {
    for (const el of armed) el.classList.add('is-in');
  }, 2500);
}

/* ---------------------------------------------------------------
   Drag to compare
   --------------------------------------------------------------- */

function compare() {
  for (const box of document.querySelectorAll('[data-cacao-compare]')) {
    const range = box.querySelector('[data-cacao-cmp-range]');
    const stage = box.querySelector('.cacao-cmp__stage');
    if (!range || !stage) continue;

    const paint = () => stage.style.setProperty('--cacao-split', `${range.value}%`);
    paint();
    range.addEventListener('input', paint);

    /* Dragging anywhere on the picture drives the same input, so the keyboard
       and the pointer stay in sync and there is only one source of truth. */
    const fromPointer = (event) => {
      const r = stage.getBoundingClientRect();
      const pct = ((event.clientX - r.left) / r.width) * 100;
      range.value = String(Math.max(0, Math.min(100, Math.round(pct))));
      paint();
    };
    stage.addEventListener('pointerdown', (event) => {
      stage.setPointerCapture(event.pointerId);
      fromPointer(event);
    });
    stage.addEventListener('pointermove', (event) => {
      if (event.buttons) fromPointer(event);
    });

    /* The melt moment: once, on arrival, and only if motion is allowed. */
    once(box, (t) => {
      if (canAnimate()) t.classList.add('is-melting');
    });
  }
}

/* ---------------------------------------------------------------
   Choose your ritual
   --------------------------------------------------------------- */

function ritual() {
  for (const box of document.querySelectorAll('[data-cacao-ritual]')) {
    const tabs = [...box.querySelectorAll('[data-cacao-rit-tab]')];
    const panels = [...box.querySelectorAll('[data-cacao-rit-panel]')];
    if (tabs.length < 2 || tabs.length !== panels.length) continue;

    const select = (i, focus) => {
      tabs.forEach((tab, n) => {
        const on = n === i;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;            // roving tabindex
        panels[n].hidden = !on;
        panels[n].classList.toggle('is-on', on);
      });
      /* Restart the ring by removing and re-adding the class. */
      const ring = panels[i].querySelector('.cacao-rit__ring');
      if (ring && canAnimate()) {
        ring.classList.remove('is-filling');
        void ring.getBoundingClientRect();
        ring.classList.add('is-filling');
      }
      if (focus) tabs[i].focus();
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(i, false));
      tab.addEventListener('keydown', (event) => {
        const next = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (next) {
          event.preventDefault();
          select((i + next + tabs.length) % tabs.length, true);
        } else if (event.key === 'Home') {
          event.preventDefault();
          select(0, true);
        } else if (event.key === 'End') {
          event.preventDefault();
          select(tabs.length - 1, true);
        }
      });
    });

    /* Swiping the photo moves between tabs on a phone. */
    let x0 = null;
    const media = box.querySelector('.cacao-rit__panel');
    box.addEventListener('touchstart', (e) => { x0 = e.changedTouches[0].clientX; }, { passive: true });
    box.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) < 48) return;
      const cur = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      select((cur + (dx < 0 ? 1 : -1) + tabs.length) % tabs.length, false);
    }, { passive: true });
    void media;

    select(0, false);
  }
}

/* ---------------------------------------------------------------
   Count up
   --------------------------------------------------------------- */

function countUp() {
  for (const el of document.querySelectorAll('[data-cacao-count]')) {
    const target = Number(el.getAttribute('data-cacao-count'));
    if (!Number.isFinite(target)) continue;
    const final = el.textContent;
    once(el, () => {
      if (!canAnimate()) return;
      const started = performance.now();
      const dur = 900;
      const tick = (now) => {
        const t = Math.min(1, (now - started) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        if (t < 1) {
          el.textContent = String(Math.round(target * eased));
          requestAnimationFrame(tick);
        } else {
          el.textContent = final;            // always land on the real string
        }
      };
      requestAnimationFrame(tick);
    });
  }
}

/* ---------------------------------------------------------------
   How long will a pack last?
   --------------------------------------------------------------- */

function calculator() {
  for (const box of document.querySelectorAll('[data-cacao-calc]')) {
    const people = box.querySelector('[data-cacao-people]');
    const cups = box.querySelector('[data-cacao-cups]');
    const out = box.querySelector('[data-cacao-calc-out]');
    const controls = box.querySelector('[data-cacao-calc-controls]');
    const stat = box.querySelector('[data-cacao-calc-static]');
    if (!people || !cups || !out || !controls) continue;

    const n = (k, d) => {
      const v = Number(box.getAttribute(k));
      return Number.isFinite(v) && v > 0 ? v : d;
    };
    const SL = n('data-starter-low', 20), SH = n('data-starter-high', 30);
    const AL = n('data-ayllu-low', 40), AH = n('data-ayllu-high', 60);

    const weeks = (cupsInPack, perWeek) => Math.max(1, Math.round(cupsInPack / perWeek));
    const span = (low, high, perWeek) => {
      const a = weeks(low, perWeek);
      const b = weeks(high, perWeek);
      return a === b ? `${a} ${a === 1 ? 'week' : 'weeks'}` : `${a}–${b} weeks`;
    };

    const clamp = (input) => {
      const v = Math.round(Number(input.value) || Number(input.min));
      return Math.max(Number(input.min), Math.min(Number(input.max), v));
    };

    const update = () => {
      people.value = String(clamp(people));
      cups.value = String(clamp(cups));
      const perWeek = Number(people.value) * Number(cups.value);
      box.querySelector('[data-cacao-res-starter]').textContent = span(SL, SH, perWeek);
      box.querySelector('[data-cacao-res-ayllu]').textContent = span(AL, AH, perWeek);
    };

    controls.hidden = false;
    out.hidden = false;
    if (stat) stat.hidden = true;
    update();

    box.addEventListener('input', (e) => {
      if (e.target === people || e.target === cups) update();
    });
    box.addEventListener('click', (e) => {
      const step = e.target.closest('[data-cacao-step]');
      if (step) {
        const input = document.getElementById(step.getAttribute('data-for'));
        if (input) {
          input.value = String(Number(input.value) + Number(step.getAttribute('data-cacao-step')));
          update();
        }
        return;
      }
      const pick = e.target.closest('[data-cacao-pick]');
      if (pick) choosePack(box.getAttribute(`data-${pick.getAttribute('data-cacao-pick')}-variant-word`) || pick.getAttribute('data-cacao-pick'));
    });
  }
}

/**
 * Select a pack through Horizon's own picker, then return to the product area.
 * Matching is on the option value text, so no variant ID is hard coded.
 */
function choosePack(word) {
  const needle = String(word || '').toLowerCase().trim();
  if (!needle) return;
  const inputs = [...document.querySelectorAll('input[data-cacao-pack]')];
  const hit = inputs.find((i) => (i.value || '').toLowerCase().includes(needle));
  if (hit && !hit.checked) {
    hit.checked = true;
    /* Horizon listens for `change` on these radios; dispatching it drives the
       real variant-picker rather than duplicating its work. */
    hit.dispatchEvent(new Event('change', { bubbles: true }));
  }
  toProduct();
}

function toProduct() {
  const target = document.querySelector('[data-cacao-scope]');
  if (!target) return;
  target.scrollIntoView({ behavior: canAnimate() ? 'smooth' : 'auto', block: 'start' });
}

/* ---------------------------------------------------------------
   Journey, lab rows, hotspots, closing button
   --------------------------------------------------------------- */

/* ---------------------------------------------------------------
   The route map
   --------------------------------------------------------------- */

function route() {
  for (const box of document.querySelectorAll('[data-cacao-route]')) {
    const tabs = [...box.querySelectorAll('[data-cacao-route-tab]')];
    const cards = [...box.querySelectorAll('[data-cacao-route-card]')];
    const pins = [...box.querySelectorAll('[data-cacao-route-pin]')];
    if (!cards.length) continue;

    const select = (i, focus) => {
      tabs.forEach((t, n) => {
        const on = n === i;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      });
      cards.forEach((c, n) => {
        c.hidden = n !== i;
        c.classList.toggle('is-on', n === i);
      });
      pins.forEach((p, n) => p.classList.toggle('is-on', n === i));
      if (focus && tabs[i]) tabs[i].focus();
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(i, false));
      tab.addEventListener('keydown', (e) => {
        const d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (d) { e.preventDefault(); select((i + d + tabs.length) % tabs.length, true); }
        else if (e.key === 'Home') { e.preventDefault(); select(0, true); }
        else if (e.key === 'End') { e.preventDefault(); select(tabs.length - 1, true); }
      });
    });

    /* The pins are SVG, so they get an explicit role and key handling rather
       than relying on a <button> we cannot nest inside <svg>. */
    pins.forEach((pin, i) => {
      pin.setAttribute('role', 'button');
      pin.setAttribute('tabindex', '0');
      pin.setAttribute('aria-label', cards[i]?.querySelector('.cacao-route__name')?.textContent?.trim() || `Stop ${i + 1}`);
      pin.addEventListener('click', () => select(i, false));
      pin.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(i, false); }
      });
    });

    /* Swipe the cards on a phone. */
    let x0 = null;
    box.addEventListener('touchstart', (e) => { x0 = e.changedTouches[0].clientX; }, { passive: true });
    box.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) < 48) return;
      const cur = cards.findIndex((c) => !c.hidden);
      select((cur + (dx < 0 ? 1 : -1) + cards.length) % cards.length, false);
    }, { passive: true });

    select(0, false);

    once(box, (t) => {
      if (!canAnimate()) return;
      t.classList.add('is-drawn');
      /* SMIL with begin="indefinite" never starts on its own, so reduced
         motion simply never reaches this line. */
      const motion = t.querySelector('[data-cacao-route-motion]');
      const dot = t.querySelector('.cacao-route__traveller');
      if (motion && typeof motion.beginElement === 'function') {
        if (dot) dot.style.opacity = '1';
        try { motion.beginElement(); } catch { /* older engine, no travel */ }
      }
    });
  }
}

/* ---------------------------------------------------------------
   Homepage: the process strip's gold line
   --------------------------------------------------------------- */

function processStrip() {
  for (const box of document.querySelectorAll('[data-cacao-process]')) {
    const strip = box.querySelector('[data-cacao-process-strip]');
    const fill = box.querySelector('[data-cacao-process-fill]');
    if (!strip || !fill) continue;

    /* The strip is native overflow scrolling, so it already swipes without
       this. All this does is follow it with the line. */
    const paint = () => {
      const max = strip.scrollWidth - strip.clientWidth;
      const pct = max > 4 ? (strip.scrollLeft / max) * 100 : 100;
      fill.style.width = `${Math.max(6, Math.min(100, pct))}%`;
    };
    paint();
    strip.addEventListener('scroll', () => requestAnimationFrame(paint), { passive: true });
    window.addEventListener('resize', () => requestAnimationFrame(paint));
  }
}

/* ---------------------------------------------------------------
   Homepage: goodbye strike-through, how-to-enjoy, lazy video
   --------------------------------------------------------------- */

function goodbye() {
  for (const el of document.querySelectorAll('[data-cacao-goodbye]')) {
    once(el, (t) => { if (canAnimate()) t.classList.add('is-struck'); });
  }
}

function enjoySteps() {
  for (const btn of document.querySelectorAll('[data-cacao-enjoy-step]')) {
    btn.addEventListener('click', () => {
      if (!canAnimate()) return;
      btn.classList.remove('is-playing');
      void btn.getBoundingClientRect();
      btn.classList.add('is-playing');
    });
  }
}

/* A muted loop is only worth its bytes while it is on screen. */
function inViewVideo() {
  for (const v of document.querySelectorAll('[data-cacao-invideo]')) {
    if (!('IntersectionObserver' in window)) continue;
    new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && canAnimate()) e.target.play?.().catch(() => {});
        else e.target.pause?.();
      }
    }, { threshold: 0.35 }).observe(v);
  }
}

/* ---------------------------------------------------------------
   Homepage pack cards: add to cart without leaving the page
   --------------------------------------------------------------- */

/**
 * The pack cards are plain product forms, so with no JavaScript they post to
 * /cart/add and Shopify takes the reader to the cart -- which works. With
 * JavaScript we do what Horizon's own product form does instead: post to
 * /cart/add.js and dispatch the cart event the cart drawer listens for, so
 * the drawer slides open and the reader stays on the page.
 */
function homePackForms() {
  for (const form of document.querySelectorAll('.home-pack__form')) {
    form.addEventListener('submit', (event) => {
      const button = form.querySelector('button[type="submit"]');
      if (button?.disabled) return;
      event.preventDefault();

      const body = new FormData(form);
      const quantity = Number(body.get('quantity')) || 1;
      const merchandiseId = String(body.get('id') || '');
      if (!merchandiseId) return;

      /* The subscribe form carries a selling_plan; the one-time form does not,
         and must not -- sending an empty one would be a different order. Read
         it off the form rather than assuming, so the line that reaches the
         cart is the one the reader chose. */
      const sellingPlan = String(body.get('selling_plan') || '').trim();
      const line = { id: merchandiseId, quantity };
      if (sellingPlan) line.selling_plan = Number(sellingPlan);

      const deferred = CartLinesUpdateEvent.createPromise();
      button?.setAttribute('aria-busy', 'true');

      /* Dispatched on document, which is where cart-drawer.js listens.
         CartLinesUpdateEvent sets its own bubbles flag -- passing one throws. */
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId, quantity, sellingPlanId: sellingPlan || undefined }],
          promise: deferred.promise,
        })
      );

      fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [line] }),
      })
        .then((r) => r.json())
        .then(() =>
          fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart.js` : '/cart.js')
            .then((r) => r.json())
        )
        .then((cart) => {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              didError: false,
              items: cart.items,
              source: 'home-pack-card',
              sourceId: form.id,
              itemCount: quantity,
            },
          });
        })
        .catch(() => {
          /* The add failed, so fall back to the real form post rather than
             leaving the reader with a button that did nothing. */
          form.submit();
        })
        .finally(() => button?.removeAttribute('aria-busy'));
    });
  }
}

/* The "see how long each lasts" link. The anchor already works on its own;
   this only makes the jump smooth, and not under reduced motion. */
function calcLink() {
  for (const a of document.querySelectorAll('[data-cacao-to-calc]')) {
    a.addEventListener('click', (e) => {
      const target = document.querySelector('#cacao-calc');
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: canAnimate() ? 'smooth' : 'auto', block: 'start' });
    });
  }
}

/* ---------------------------------------------------------------
   Homepage: the sticky bar and the review topic pills
   --------------------------------------------------------------- */

/* The bar is hidden in the markup and only revealed once the hero has gone
   by, so it never covers anything before the reader has scrolled. */
function stickyBar() {
  const bar = document.querySelector('[data-cacao-stickybar]');
  const hero = document.querySelector('.home-hero');
  if (!bar || !hero) return;
  if (!('IntersectionObserver' in window)) { bar.hidden = false; return; }
  new IntersectionObserver(
    ([e]) => { bar.hidden = e.isIntersecting; },
    { threshold: 0, rootMargin: '0px' }
  ).observe(hero);
}

/* Topic pills filter the review cards. Without script every card is shown and
   the pills are hidden, so nothing is ever unreachable. */
function reviewTabs() {
  for (const box of document.querySelectorAll('[data-cacao-reviewtabs]')) {
    const pills = [...box.querySelectorAll('[data-cacao-rt-pill]')];
    const cards = [...box.querySelectorAll('[data-cacao-rt-card]')];
    if (!pills.length) return;

    const show = (topic, focus) => {
      pills.forEach((pill) => {
        const on = pill.dataset.topic === topic;
        pill.setAttribute('aria-selected', String(on));
        pill.tabIndex = on ? 0 : -1;
        if (on && focus) pill.focus();
      });
      cards.forEach((card) => {
        card.hidden = !(card.dataset.topics || '').split(',').map((t) => t.trim()).includes(topic);
      });
    };

    pills.forEach((pill, i) => {
      pill.addEventListener('click', () => show(pill.dataset.topic, false));
      pill.addEventListener('keydown', (e) => {
        const d = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
        if (!d) return;
        e.preventDefault();
        show(pills[(i + d + pills.length) % pills.length].dataset.topic, true);
      });
    });
    show(pills[0].dataset.topic, false);
  }
}

function weave() {
  for (const el of document.querySelectorAll('[data-cacao-weave]')) {
    once(el, (t) => { if (canAnimate()) t.classList.add('is-woven'); });
  }
}

function labRows() {
  for (const el of document.querySelectorAll('[data-cacao-lab]')) {
    once(el, (t) => {
      if (canAnimate()) t.classList.add('is-ticking');
    });
  }
}

function hotspots() {
  for (const dot of document.querySelectorAll('[data-cacao-hotspot]')) {
    dot.addEventListener('click', () => {
      const open = dot.classList.contains('is-open');
      for (const d of dot.closest('[data-cacao-flat]').querySelectorAll('[data-cacao-hotspot]')) {
        d.classList.remove('is-open');
      }
      dot.classList.toggle('is-open', !open);
    });
  }
}

function closingButton() {
  for (const a of document.querySelectorAll('[data-cacao-to-product]')) {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      toProduct();
    });
  }
}

/* ---------------------------------------------------------------
   Judge.me "write a review"
   --------------------------------------------------------------- */

function writeReview() {
  for (const a of document.querySelectorAll('[data-cacao-write-review]')) {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      /* Judge.me binds its own handler to .jdgm-write-rev-link when the widget
         is on the page. If it has not loaded, send the reader to the widget
         rather than doing nothing. */
      const form = document.querySelector('.jdgm-form-wrapper, .jdgm-rev-widg');
      form?.scrollIntoView({ behavior: canAnimate() ? 'smooth' : 'auto', block: 'center' });
    });
  }
}

/**
 * Horizon's sticky Add to cart renders an icon and an empty text span, so the
 * button has no accessible name at all -- a screen reader announces "button".
 * This is Horizon's own markup and it is identical on the live theme; the
 * round 3b layout only makes it visible early enough for an audit to catch it.
 * Rather than fork the theme's snippet, borrow the name from the real Add to
 * cart button, which is server-rendered and already correct.
 */
function nameStickyButton() {
  const sticky = document.querySelector('.sticky-add-to-cart__button');
  if (!sticky || (sticky.textContent || '').trim() || sticky.getAttribute('aria-label')) return;
  const main = document.querySelector('button[name="add"]');
  const label = (main?.textContent || '').trim();
  if (label) sticky.setAttribute('aria-label', label);
}

function init() {
  reveals();
  nameStickyButton();
  compare();
  ritual();
  countUp();
  calculator();
  route();
  weave();
  processStrip();
  goodbye();
  enjoySteps();
  inViewVideo();
  homePackForms();
  calcLink();
  stickyBar();
  reviewTabs();
  labRows();
  hotspots();
  closingButton();
  writeReview();
  articleToc();
  articleCardPlacement();
}

/* ---------------------------------------------------------------
   Journal article: contents, and the product card's real position
   --------------------------------------------------------------- */

/**
 * Build the table of contents from the article's own H2s.
 *
 * Done here rather than in Liquid because the alternative is splitting the
 * post's HTML on "<h2" to inject ids, which breaks the first time someone
 * pastes a heading that carries an attribute. The article reads perfectly
 * without the list, so the <nav> ships hidden and only appears once there is
 * something real to put in it.
 */
function articleToc() {
  const nav = document.querySelector('[data-cacao-toc]');
  const list = document.querySelector('[data-cacao-toc-list]');
  const body = document.querySelector('[data-cacao-art-body]');
  if (!nav || !list || !body) return;

  const heads = [...body.querySelectorAll('h2')].filter((h) => h.textContent.trim().length > 1);
  if (heads.length < 3) return;          // two headings is a shape, not a contents

  const used = new Set();
  heads.forEach((h, i) => {
    let id = h.id;
    if (!id) {
      id =
        h.textContent
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 48) || `section-${i + 1}`;
      while (used.has(id)) id = `${id}-${i + 1}`;
      h.id = id;
    }
    used.add(id);

    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `#${id}`;
    a.textContent = h.textContent.trim();
    li.appendChild(a);
    list.appendChild(li);
  });

  nav.hidden = false;
}

/**
 * Move the first product card to after the opening paragraph.
 *
 * Server-side it sits above the article, because Liquid cannot see where the
 * intro ends. With script we can: it goes after the first paragraph long
 * enough to be an intro rather than a one-line kicker.
 */
function articleCardPlacement() {
  const card = document.querySelector('.cacao-artcard--top');
  const body = document.querySelector('[data-cacao-art-body]');
  if (!card || !body) return;
  const paras = [...body.children].filter((el) => el.tagName === 'P' && el.textContent.trim().length > 120);
  const after = paras[0];
  if (after) after.insertAdjacentElement('afterend', card);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
